import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SubscriptionPlanService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching subscription plan list.
 *
 * @param {Object} params - Query parameters (page, limit, search, status, user_type)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscriptionPlanList = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["subscription-plan-list", params],
    queryFn: () => {
      return SubscriptionPlanService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single subscription plan.
 *
 * @param {string} uuid - Subscription Plan UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscriptionPlan = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["subscription-plan", uuid],
    queryFn: () => {
      return SubscriptionPlanService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for adding a new subscription plan.
 *
 * @param {function} onSuccess - Callback to run if the add request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the add request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useAddSubscriptionPlan = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return SubscriptionPlanService.add(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plan-list"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for updating an existing subscription plan.
 *
 * @param {function} onSuccess - Callback to run if the update request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the update request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateSubscriptionPlan = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return SubscriptionPlanService.update(data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plan-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-plan", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for toggling a subscription plan's active/inactive status.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateSubscriptionPlanStatus = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, isActive }) => {
      return SubscriptionPlanService.updateStatus(uuid, isActive);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plan-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-plan", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for deleting a subscription plan.
 *
 * @param {function} onSuccess - Callback to run if the delete request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the delete request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useDeleteSubscriptionPlan = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return SubscriptionPlanService.delete(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plan-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-plan", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useSubscriptionPlanList,
  useSubscriptionPlan,
  useAddSubscriptionPlan,
  useUpdateSubscriptionPlan,
  useUpdateSubscriptionPlanStatus,
  useDeleteSubscriptionPlan,
};
